import { Button } from 'components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from 'components/ui/dialog';
import { cn } from 'lib/utils';

import ProjectConfig from '../../constants';

interface ContactSupportModalProps {
	isOpen: boolean;
	onClose: () => void;
	isMobileScreen: boolean;
	translate: (key: string) => string;
	/**
	 * ⚠️ No se usa. El botón del asistente lleva comentado desde antes de esta migración, pero
	 * `Dashboard.tsx:1110` sigue pasando la prop, así que quitarla de aquí rompería los tipos
	 * allí. Se mantiene para no arrastrar ese cambio a una migración de estilos: o vuelve el
	 * botón, o se quitan las dos a la vez.
	 */
	onOpenAIAssistant?: () => void;
}

/**
 * Los tres botones llevaban `colorScheme` de Chakra —`whatsapp`, `blue`, `pink`—, que Proa no
 * tiene. Se conservan los colores exactos en lugar de repartirlos entre las variantes del sistema:
 * cambiar aquí de color es una decisión de diseño, y esta tarea cambia de qué están hechas las
 * pantallas, no qué aparentan. Los valores salen del tema real de Chakra (`@chakra-ui/theme`), no
 * de memoria: whatsapp.500 #22c35e / .600 #179848, blue.500 #3182ce / .600 #2b6cb0,
 * pink.500 #D53F8C / .600 #B83280.
 *
 * El verde de WhatsApp, además, es color de marca ajena: no debería salir nunca de nuestros
 * tokens aunque el sistema tuviera un verde parecido.
 */
const COLORES = {
	whatsapp: 'bg-[#22c35e] text-white hover:bg-[#179848]',
	soporte: 'bg-[#3182ce] text-white hover:bg-[#2b6cb0]',
	correo: 'bg-[#D53F8C] text-white hover:bg-[#B83280]'
};

const ContactSupportModal: React.FC<ContactSupportModalProps> = ({
	isOpen,
	onClose,
	isMobileScreen,
	translate
}) => {
	// El Dialog de Radix avisa de apertura Y cierre; `onClose` solo espera el cierre.
	const alCambiar = (abierto: boolean) => {
		if (!abierto) onClose();
	};

	const tamaño = isMobileScreen ? 'lg' : 'default';

	return (
		<Dialog open={isOpen} onOpenChange={alCambiar}>
			{/*
			 * `translate-none` no es decorativo: el DialogContent viene centrado con
			 * `left-[50%] top-[50%]` más un desplazamiento de media caja, y este modal va anclado a
			 * una esquina. Sin anularlo se queda media caja hacia arriba y a la izquierda de donde
			 * toca — medido: 150 px en horizontal y 100 px en vertical.
			 *
			 * 🔴 Antes ponía `[transform:none]`, y dejó de servir al traer el `dialog` del registry
			 * el 26/09/26. El componente pasó de `[transform:translate(-50%,-50%)]` a
			 * `translate-x-[-50%] translate-y-[-50%]`, y en Tailwind 4 esas utilidades NO compilan a
			 * `transform`: compilan a la propiedad `translate`. Al ser propiedades distintas,
			 * `transform: none` dejaba el centrado intacto — y tailwind-merge tampoco las
			 * deduplicaba, porque para él son grupos distintos.
			 *
			 * Con `translate-none` sí: mismo grupo, gana el último, y `cn()` quita las dos clases de
			 * centrado de la lista. Comprobado con el tailwind-merge del propio repositorio.
			 *
			 * ⚠️ Nada de esto lo veía ninguna comprobación: `tsc` no mira CSS, jsdom no maqueta y
			 * ninguna captura abre este modal. En móvil, con `inset-x-0`, se habría salido de la
			 * pantalla.
			 */}
			<DialogContent
				className={cn(
					'translate-none',
					isMobileScreen
						? 'inset-x-0 bottom-0 top-auto max-h-[50vh] max-w-full overflow-y-auto rounded-b-none rounded-t-md sm:rounded-b-none sm:rounded-t-md'
						: // Ancho y alto limitados al hueco real: el traspaso monta esta variante también en
							// un móvil, y con `max-w-md` a secas se salía 20 px por la izquierda y, en una
							// pantalla baja, por arriba con «Cerrar» fuera.
							'bottom-[120px] left-auto right-[20px] top-auto max-h-[calc(100dvh-140px)] max-w-[min(28rem,calc(100vw-40px))] overflow-y-auto rounded-md sm:rounded-md'
				)}
			>
				{/* `pr-12`, no `pr-8`: la X del DialogContent ocupa de 2 a 46 px desde la esquina
					    desde que trae área táctil de 44 px, así que 32 px de margen dejaban el
					    título por debajo de la zona pulsable. */}
				<DialogHeader className="pr-12">
					<DialogTitle>{translate('general.chat.title')}</DialogTitle>
				</DialogHeader>

				<p className="mb-4">{translate('general.chat.message')}</p>

				<div className="mb-6 flex flex-col gap-3">
					<Button asChild size={tamaño} className={cn('w-full', COLORES.whatsapp)}>
						<a
							href={`https://api.whatsapp.com/send/?phone=${ProjectConfig.ARCA_CONTACT.PHONE_FOR_WHATSAPP_LINK}&text&type=phone_number&app_absent=0`}
							target="_blank"
							rel="noopener noreferrer"
						>
							<img
								src="/imgs/whatsapp-icon.png"
								alt=""
								className="size-6 rounded-full object-cover"
							/>
							{translate('general.chat.whatsapp') || 'Contactar por WhatsApp'}
						</a>
					</Button>

					<Button asChild size={tamaño} className={cn('w-full', COLORES.soporte)}>
						<a href="https://support.arcadigital.app/" target="_blank" rel="noopener noreferrer">
							📖 {translate('general.chat.support')}
						</a>
					</Button>

					<Button asChild size={tamaño} className={cn('w-full', COLORES.correo)}>
						<a href={`mailto:${ProjectConfig.ARCA_CONTACT.EMAIL}`}>
							✉️ {translate('general.chat.contact')}
						</a>
					</Button>

					{/*
					 * Había dos botones más comentados y se han quitado, porque el código muerto
					 * comentado no se migra: está en git. Lo que sí se conserva es la intención,
					 * que es lo único que el historial no cuenta de un vistazo:
					 *   · el del asistente Noel·IA (clave 'general.chat.ai');
					 *   · el de llamar por teléfono (clave 'general.chat.call'), marcado como
					 *     «temporalmente deshabilitada», así que alguien esperaba recuperarlo.
					 */}
				</div>
			</DialogContent>
		</Dialog>
	);
};

export default ContactSupportModal;
